import { InfoDot } from '../../components/common';
import { Term } from '../../components/Term';
import { areaLabel, divergenceLine, type Divergence } from './compare';
import {
  SHARED_AREAS,
  SUN_SPLIT_TITLE,
  VEDIC_ONLY,
  VERDICT_LABEL,
  WESTERN_ONLY,
  WHICH_IS_RIGHT,
  sunSplitLine,
  systemName,
} from './copy';

/**
 * The "Both" view's reason to exist.
 *
 * Every competitor ships one zodiac and hopes nobody notices the other. Showing
 * both immediately raises the question the reader deserves an answer to — "so
 * which one is right?" — and the honest answer is that they measure from
 * different origins.
 *
 * Nothing here is asserted and nothing is generated: `compareReadings` derived
 * all of it from the two already-computed readings and the chart, including the
 * ayanamsa gap, which is this chart's own number rather than a rounded claim.
 * `divergenceLine` owns the sentence so its two branches — they agree / they do
 * not — can never drift apart.
 *
 * Deliberately NOT wrapped in a single `data-mode`: this card is about the pair,
 * so the two tradition palettes appear side by side inside it instead.
 */

function AreaChips({ areas, mode }: { areas: string[]; mode?: 'vedic' | 'western' }) {
  return (
    <div className="wrap" style={{ gap: 6, marginTop: 4 }} data-mode={mode}>
      {areas.map((label) => (
        <span
          key={label}
          className="chip"
          style={{
            cursor: 'default',
            minHeight: 0,
            padding: '4px 10px',
            fontSize: '0.75rem',
            background: 'var(--mode-soft)',
            borderColor: 'var(--mode-line)',
            color: 'var(--mode-ink)',
          }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export function DivergenceCard({ divergence }: { divergence: Divergence }) {
  const d = divergence;
  return (
    <div className="card card--strong">
      <div className="spread" style={{ alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <span className="h3">{VERDICT_LABEL[d.verdict]}</span>
        <span className="tiny faint" style={{ flexShrink: 0 }}>
          {d.ayanamsaDeg.toFixed(1)}° apart
        </span>
      </div>

      {/* The one line, plus the term that explains the gap it names. */}
      <div className="small" style={{ lineHeight: 1.6 }}>
        {divergenceLine(d)}
        <InfoDot term="precession" />
      </div>

      {d.sun && (
        <>
          <div className="divider" style={{ margin: '12px 0' }} />
          <div className="tiny faint" style={{ marginBottom: 6 }}>
            {SUN_SPLIT_TITLE}
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
            <div data-mode="vedic" style={{ flex: 1, minWidth: 0 }}>
              <span className="mode-chip">{systemName('vedic')}</span>
              <div className="small" style={{ color: 'var(--mode-ink)', fontWeight: 600, marginTop: 6 }}>
                {/* `sidereal` is a rashi name (Karka…); its per-item glossary id
                    is the lowercased name. An unknown id degrades to plain text. */}
                <Term id={`rashi.${d.sun.sidereal.toLowerCase()}`}>{d.sun.sidereal}</Term>
              </div>
            </div>
            <div data-mode="western" style={{ flex: 1, minWidth: 0 }}>
              <span className="mode-chip">{systemName('western')}</span>
              <div className="small" style={{ color: 'var(--mode-ink)', fontWeight: 600, marginTop: 6 }}>
                {d.sun.tropical}
              </div>
            </div>
          </div>
          <div className="tiny muted" style={{ lineHeight: 1.55, marginTop: 8 }}>
            {sunSplitLine(d.sun.sidereal, d.sun.tropical, d.sun.differs)}
          </div>
        </>
      )}

      {(d.shared.length > 0 || d.vedicOnly.length > 0 || d.westernOnly.length > 0) && (
        <>
          <div className="divider" style={{ margin: '12px 0' }} />
          {d.shared.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <span className="tiny faint">{SHARED_AREAS}</span>
              <AreaChips areas={d.shared.map((o) => areaLabel(o.area))} />
            </div>
          )}
          {d.vedicOnly.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <span className="tiny faint">{VEDIC_ONLY}</span>
              <AreaChips areas={d.vedicOnly.map(areaLabel)} mode="vedic" />
            </div>
          )}
          {d.westernOnly.length > 0 && (
            <div>
              <span className="tiny faint">{WESTERN_ONLY}</span>
              <AreaChips areas={d.westernOnly.map(areaLabel)} mode="western" />
            </div>
          )}
        </>
      )}

      <div className="tiny faint" style={{ lineHeight: 1.5, marginTop: 12 }}>
        {WHICH_IS_RIGHT}
      </div>
    </div>
  );
}
